import Mousetrap from 'mousetrap';

import React, { useEffect, useState } from 'react';
import { FormContainer, TextFieldElement, useForm } from 'react-hook-form-mui';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Trans, t } from '@lingui/macro';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { Image, posaleService, validators } from '@ft-frontend/core';

import LanguageSelector from 'components/LanguageSelector';
import PosaleFooter from 'components/PosaleFooter';

import {
  adminStoresUrl,
  changePasswordUrl,
  loginRecoverUrl,
  servicesUrl,
} from 'constants/routes';
import { ADMIN } from 'constants/user';

import useStartingInfo from 'contexts/starting_info/useStartingInfo';

import { usePosaleAPI } from 'hooks/usePosaleAPI';

import { login } from 'reducers/redux/reduxSlice';

export const loginStatus = { inactive: 0, active: 1, pending: 2, blocked: 3 };

export default function Login() {
  const whitelabel = useStartingInfo();
  const { hasSingleSignOn } = useSelector(
    (state) => state.redux.whitelabel.configs,
  );
  const [singleSignOn, setSingleSignOn] = useState(hasSingleSignOn);
  const {
    data: loginData,
    isLoading: isLoadingLogin,
    isError: isErrorLogin,
    triggerRequest: loginRequest,
  } = usePosaleAPI(posaleService.endpoints.login, {
    initialRequest: false,
  });
  const { data: userInfoData, triggerRequest: getUserInfo } = usePosaleAPI(
    posaleService.endpoints.getUserInfo,
    {
      initialRequest: false,
    },
  );
  const {
    data: singleSignOnData,
    isLoading: isLoadingSingleSignOn,
    isError: isErrorSingleSignOn,
    triggerRequest: singleSignOnRequest,
  } = usePosaleAPI(posaleService.endpoints.singleSignOn, {
    initialRequest: false,
  });

  const loginFormContext = useForm({
    mode: 'onChange',
  });
  const {
    handleSubmit,
    formState: { isDirty, isValid },
  } = loginFormContext;
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { SSOToken } = useParams();

  useEffect(() => {
    if ((loginData && !SSOToken) || (userInfoData && SSOToken)) {
      const data = SSOToken ? userInfoData : loginData;

      if (data.session_token) {
        dispatch(
          login({
            ...data,
            status: data.loginStatus,
            selectedStore: data.default_store,
          }),
        );
        if (data.loginStatus === loginStatus.pending) {
          navigate(changePasswordUrl);
        } else {
          if (!data.wlHasStores) {
            if (data.role === ADMIN) {
              navigate(adminStoresUrl);
            } else {
              window.location.href = servicesUrl;
              // TODO - change after migration to -> navigate(servicesUrl);
            }
          } else {
            window.location.href = servicesUrl;
            // TODO - change after migration to -> navigate(servicesUrl);
          }
        }
      }
    }
  }, [SSOToken, dispatch, loginData, navigate, userInfoData]);

  useEffect(() => {
    if (SSOToken) {
      getUserInfo({ token: SSOToken });
    }
  }, [SSOToken, getUserInfo]);

  useEffect(() => {
    if (hasSingleSignOn) {
      Mousetrap.bind(['alt+l'], () => {
        setSingleSignOn((singleSignOn) => !singleSignOn);
      });

      return () => {
        Mousetrap.unbind(['alt+l']);
      };
    }
  }, [hasSingleSignOn]);

  useEffect(() => {
    if (singleSignOn && singleSignOnData.redirectUrl) {
      navigate(singleSignOnData.redirectUrl);
    }
  }, [navigate, singleSignOn, singleSignOnData.redirectUrl]);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };
  const onSubmit = async (data) => {
    if (singleSignOn) {
      singleSignOnRequest({});
    } else {
      loginRequest({ login: data.email, password: data.password });
    }
  };

  return (
    <Stack
      sx={(theme) => ({
        margin: `${theme.spacing(3)} ${theme.spacing(2)} 0`,
        flexGrow: 1,
      })}
      justifyContent='space-between'
      alignItems='center'
      spacing={1}
    >
      <Box sx={{ width: '100%' }}>
        <LanguageSelector
          visible={whitelabel.configs?.canChangeLanguage}
          availableLocales={whitelabel.configs?.availableLocales}
          defaultLanguage={whitelabel.configs?.defaultLanguage}
        />
      </Box>
      <Stack spacing={5}>
        <Image
          filePath={whitelabel.posaleSecondaryLogo}
          alt='TD SYNNEX'
          width={176}
          style={{ maxWidth: '176px' }}
          pictureStyle={{ alignSelf: 'center' }}
        />
        <Typography variant='h4' sx={{ textAlign: 'center' }}>
          <Trans id='login.title'>Log In</Trans>
        </Typography>

        <Stack spacing={4}>
          {singleSignOn ? (
            <Typography>
              <Trans id='login.sso.desc'>
                Use your company login solution.
              </Trans>
            </Typography>
          ) : (
            <Typography>
              <Trans id='login.manual.desc'>
                To log in use your company email address / username
              </Trans>
            </Typography>
          )}

          {!singleSignOn && (
            <>
              <FormContainer id='LoginForm' formContext={loginFormContext}>
                <Stack spacing={2}>
                  <TextFieldElement
                    data-testid='posale-login-page-username'
                    name='email'
                    label={
                      <Trans id='login.form.email'>
                        Email Address / Username
                      </Trans>
                    }
                    required
                    rules={{
                      validate: {
                        lengthValidator: validators.lengthValidator(250),
                      },
                      required: t({
                        id: 'common.form.input.mandatory',
                        message: `Mandatory Field`,
                      }),
                    }}
                  />
                  <TextFieldElement
                    name='password'
                    label={<Trans id='login.form.pwd'>Password</Trans>}
                    type={showPassword ? 'text' : 'password'}
                    data-testid='posale-login-page-password'
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle password visibility'
                            onClick={togglePasswordVisibility}
                            onMouseDown={handleMouseDownPassword}
                            edge='end'
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    rules={{
                      required: t({
                        id: 'common.form.input.mandatory',
                        message: `Mandatory Field`,
                      }),
                    }}
                  />
                </Stack>
              </FormContainer>
              <Link
                variant='subtitle1'
                color='primary'
                href={loginRecoverUrl}
                data-testid='posale-login-page-forgot-link'
                sx={(theme) => ({
                  display: 'flex',
                  direction: 'row',
                  alignItems: 'center',
                  color: theme.palette.primary.main,
                  textDecorationColor: theme.palette.primary.main,
                })}
              >
                <Trans id='login.forgot.pwd'>Forgot Password?</Trans>
              </Link>
            </>
          )}

          <Box>
            <LoadingButton
              type='submit'
              loading={
                (isLoadingLogin && !isErrorLogin && !singleSignOn) ||
                (isLoadingSingleSignOn && !isErrorSingleSignOn && singleSignOn)
              }
              data-testid='posale-login-page-login-btn'
              variant='contained'
              size='large'
              disabled={(!isDirty || !isValid) && !singleSignOn}
              onClick={handleSubmit(onSubmit)}
            >
              {singleSignOn ? (
                <Trans id='login.sso'>Log in with your employee account</Trans>
              ) : (
                <Trans id='login.button'>Login</Trans>
              )}
            </LoadingButton>
          </Box>
        </Stack>
      </Stack>
      <PosaleFooter />
    </Stack>
  );
}
